import { Box, Checkbox, MenuItem, SelectChangeEvent, TextField, useTheme } from "@mui/material";
import Autocomplete, { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { ContentType } from "@shared/mirage/metadata/Contracts";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import QueryBuilder, { Field, FieldSelectorProps, OperatorSelectorProps, QueryBuilderProps, toFullOption, ValueEditorProps } from "react-querybuilder";
import "./advancedQueryBuilder.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker/DateTimePicker";
import LookupField from "../regularField/LookupField";
import { useBoolean } from "@mirage/hooks/useBoolean";
import { MirageOnChangeEvent } from "../regularField/Field";
import dayjs, { Dayjs } from "dayjs";
import { MirageConstants } from "@shared/mirage/mirageConstants";
import datePickerValueFormatter from "../helpers/datePickerValueFormatter";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { DateRange } from "@mui/x-date-pickers-pro";
import { RegexStore } from "@shared/mirage/utils/RegexStore";
import { usePicklist } from "@mirage/store/PicklistContext";

interface AdvancedQueryBuilderProps extends QueryBuilderProps<any, any, any, any> {
    fields: Field[];
}

export const AdvancedQueryBuilder = memo((props: AdvancedQueryBuilderProps) => {
    const { fields, ...otherProps } = props;
    const fieldOptions = useMemo(() => fields.map((f) => toFullOption(f)), [fields]);
    const availableOptions = [
        { label: "Is", value: "is" },
        { label: "Is Not", value: "isNot" },
        { label: "<", value: "<" },
        { label: "<=", value: "<=" },
        { label: ">", value: ">" },
        { label: ">=", value: ">=" },
        { label: "Contains", value: "contains" },
        { label: "Not Contains", value: "notContains" },
        { label: "Starts With", value: "startsWith" },
        { label: "Not Starts With", value: "notStartsWith" },
        { label: "Ends With", value: "endsWith" },
        { label: "Not Ends With", value: "notEndsWith" },
        { label: "Is Empty", value: "isEmpty" },
        { label: "Is Not Empty", value: "isNotEmpty" },
        { label: "Is Any Of", value: "isAnyOf" },
        { label: "Is Not Any Of", value: "isNotAnyOf" },
        { label: "Between", value: "between" },
        { label: "Not Between", value: "notBetween" },
        { label: "Before", value: "before" },
        { label: "After", value: "after" },
        { label: "On Or Before", value: "onOrBefore" },
        { label: "On Or After", value: "onOrAfter" },
    ];
    return (
        <QueryBuilder
            {...otherProps}
            addRuleToNewGroups
            showCombinatorsBetweenRules
            fields={fieldOptions}
            operators={availableOptions}
            controlClassnames={{ queryBuilder: "queryBuilder-branches", operators: "queryBuilder__operators" }}
            controlElements={{ valueEditor: AqbCustomValueEditor, fieldSelector: AqbCustomFieldSelector, operatorSelector: OperatorSelector }}
        />
    );
});

const OperatorSelector = memo((props: OperatorSelectorProps) => {
    const { disabled, fieldData, handleOnChange, value, options } = props;
    const inputType = fieldData?.inputType ? fieldData?.inputType.split(":")[0] : null;
    const theme = useTheme();
    const optionsByType = useMemo(() => {
        if (!options) {
            return [];
        }
        switch (inputType) {
            case "TEXT":
            case "EMAIL":
            case "PHONE":
            case "TEXTAREA":
            case "PICKLIST":
                return (options as any).filter(
                    (o: any) =>
                        ["contains", "notContains", "startsWith", "notStartsWith", "endsWith", "notEndsWith", "is", "isNot", "isEmpty", "isNotEmpty"].includes(o.value) as any
                );
            case "LOOKUP":
                return (options as any).filter((o: any) => ["is", "in", "notIn", "isEmpty", "isNotEmpty"].includes(o.value)) as any;
            case "ID":
            case "NUMBER":
                return (options as any).filter((o: any) => ["is", "isNot", "<", "<=", ">", ">=", "in", "notIn", "isEmpty", "isNotEmpty"].includes(o.value)) as any;
            case "DATE":
            case "TIME":
            case "DATETIME":
                return (options as any).filter(
                    (o: any) => ["is", "isNot", "between", "notBetween", "before", "after", "onOrBefore", "onOrAfter", "isEmpty", "isNotEmpty"].includes(o.value) as any
                );

            case "CHECKBOX":
                return (options as any).filter((o: any) => ["is", "isNot", "isEmpty", "isNotEmpty"].includes(o.value)) as any;

            default:
                return options as any;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputType]) as unknown as readonly CustomFieldSelectorOption[];
    const autocompleteOptions = useMemo(() => optionsByType || [], [optionsByType]) as unknown as readonly CustomFieldSelectorOption[];
    const readonlyInput = useCallback((params: AutocompleteRenderInputParams) => <TextField label={""} {...params} disabled={disabled} />, [disabled]);
    const onChange = useCallback(
        (e: React.SyntheticEvent<Element, Event>, value: CustomFieldSelectorOption | null) => {
            handleOnChange(value?.value);
        },
        [handleOnChange]
    );
    const getOptionLabel = useCallback((option: any) => option.label, []);
    const autocompleteValue = (options as unknown as CustomFieldSelectorOptions)?.filter(({ value: optionValue }) => value === optionValue)[0];
    const renderOption = useCallback(
        (_props: React.HTMLAttributes<HTMLLIElement>, option: CustomFieldSelectorOption) => {
            if (!option.label) {
                return null;
            }
            return (
                <Box
                    key={option.label}
                    sx={{
                        paddingLeft: "14px",
                        paddingY: "4px",
                        ":hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            cursor: "pointer",
                        },
                    }}
                    onClick={() => onChange({} as any, option)}
                >
                    {option.label}
                </Box>
            );
        },
        [onChange, theme.palette.action.hover, theme.palette.text.primary]
    );

    return (
        <Autocomplete
            sx={{ minWidth: 200 }}
            size="small"
            value={autocompleteValue}
            isOptionEqualToValue={(o, v) => o?.value?.toLowerCase().replace(RegexStore.GlobalSpace, "") === v?.value?.toLowerCase().replace(RegexStore.GlobalSpace, "")}
            options={autocompleteOptions}
            renderInput={readonlyInput}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            readOnly={disabled}
            disableClearable
        />
    );
});

const AqbCustomFieldSelector = memo((props: FieldSelectorProps) => {
    const { options, value, handleOnChange, disabled } = props;
    const copyOfOptions = useMemo(() => [...options], [options]);
    const theme = useTheme();
    const alphabeticalOptions = useMemo(() => {
        if (copyOfOptions && copyOfOptions.length) {
            return copyOfOptions.sort((a, b) => a.label?.localeCompare(b.label));
        }
        return [];
    }, [copyOfOptions]) as unknown as readonly CustomFieldSelectorOption[];
    const autocompleteOptions = useMemo(() => alphabeticalOptions || [], [alphabeticalOptions]) as unknown as readonly CustomFieldSelectorOption[];
    const readonlyInput = useCallback((params: AutocompleteRenderInputParams) => <TextField label={""} {...params} disabled={disabled} />, [disabled]);
    const onChange = useCallback(
        (e: React.SyntheticEvent<Element, Event>, value: CustomFieldSelectorOption | null) => {
            handleOnChange(value?.value);
        },
        [handleOnChange]
    );
    const getOptionLabel = useCallback((option: any) => option.label, []);
    const autocompleteValue = (options as unknown as CustomFieldSelectorOptions)?.filter(({ value: optionValue }) => value === optionValue)[0];
    const renderOption = useCallback(
        (_props: React.HTMLAttributes<HTMLLIElement>, option: CustomFieldSelectorOption) => {
            if (!option.label) {
                return null;
            }
            return (
                <Box
                    key={option.name}
                    sx={{
                        paddingLeft: "14px",
                        paddingY: "4px",
                        ":hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            cursor: "pointer",
                        },
                    }}
                    onClick={() => onChange({} as any, option)}
                >
                    {option.label}
                </Box>
            );
        },
        [onChange, theme.palette.action.hover, theme.palette.text.primary]
    );

    return (
        <Autocomplete
            sx={{ minWidth: 200 }}
            size="small"
            value={autocompleteValue}
            options={autocompleteOptions}
            renderInput={readonlyInput}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            readOnly={disabled}
        />
    );
});

const AqbCustomValueEditor = memo((props: ValueEditorProps) => {
    const { value: forceFetch, toggle: toggleForceFetch } = useBoolean(false);
    const { value, handleOnChange, field, disabled, operator } = props;
    const inputType: ContentType | null | undefined = props.inputType as ContentType | null | undefined;
    let localInputType = inputType;
    const inputIsLookupType = inputType?.includes("LOOKUP");
    let relatedObject = "";
    let picklistName = "";
    const lookupOnChange = useCallback(
        (e: MirageOnChangeEvent, val: any | null) => {
            handleOnChange({ id: val?.id, name: val?.name || "" });
        },
        [handleOnChange]
    );
    const textFieldOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleOnChange(event.target.value);
    };
    const checkBoxOnChange = (e: SelectChangeEvent, checked: boolean) => {
        const castedVal = checked ? 1 : 0;
        handleOnChange(castedVal);
    };
    const dateOnChange = (val: Dayjs | null) => {
        const castedValue = val?.set("hour", MirageConstants.DefaultDateFieldHour).set("minute", 0).set("second", 0).set("millisecond", 0).toDate() || null;
        handleOnChange(castedValue);
    };
    const dateRangeOnChange = (value: DateRange<unknown>) => {
        const [lower, upper] = value;
        const lowerDate = lower ? DateFormatter.getUTCDate(lower as Dayjs) : "";
        const upperDate = upper ? DateFormatter.getUTCDate(upper as Dayjs) : "";
        handleOnChange([lowerDate, upperDate]);
    };
    const formatValues = (): [Dayjs | null, Dayjs | null] => {
        if (!Array.isArray(value)) {
            return [null, null];
        }
        const [lower, upper] = value;
        const formattedLower = lower ? dayjs(lower) : null;
        const formattedUpper = upper ? dayjs(upper) : null;
        return [formattedLower, formattedUpper];
    };
    const picklistContext = usePicklist();

    useEffect(() => {
        toggleForceFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputType]);

    if (!inputType) {
        console.error("AqbCustomValueEditor: inputType is not defined for field", props.field);
        return null;
    }
    if (operator === "notNull" || operator === "null" || operator === "isEmpty" || operator === "isNotEmpty") {
        return null;
    }
    if (inputIsLookupType) {
        localInputType = "LOOKUP";
        relatedObject = inputType.split(":")[1];
    }
    if (inputType.includes("PICKLIST")) {
        localInputType = "PICKLIST";
        picklistName = inputType.split(":")[1];
    }

    switch (localInputType) {
        case "TEXT":
        case "EMAIL":
        case "PHONE":
        case "TEXTAREA":
            return <TextField size="small" onChange={textFieldOnChange} value={value} disabled={disabled} />;
        case "PICKLIST": {
            const contextItems = picklistName ? picklistContext.getPicklistItems(picklistName) : null;
            const contextOptions = contextItems && contextItems.length > 0 ? contextItems.map((item) => item.label) : [];

            const options = picklistName && contextOptions.length > 0 ? contextOptions : props.values;
            return (
                <TextField
                    select
                    onChange={textFieldOnChange}
                    size="small"
                    disabled={disabled}
                    value={value}
                    sx={{ minWidth: 200 }}
                    slotProps={{
                        select: {
                            displayEmpty: true,
                        },
                    }}
                >
                    {options?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            );
        }
        case "LOOKUP":
            return (
                <LookupField
                    contentType="LOOKUP"
                    relatedObject={relatedObject}
                    label=""
                    mode={disabled ? "view" : "edit"}
                    containerClassName="queryBuilder__lookupVal"
                    forceFetch={forceFetch}
                    onChange={lookupOnChange}
                    relatedRecord={value}
                />
            );
        case "ID":
        case "NUMBER":
            return <TextField size="small" type="number" onChange={textFieldOnChange} value={value} disabled={disabled} />;
        case "DATE":
        case "TIME":
        case "DATETIME": {
            if (operator !== "between" && operator !== "notBetween") {
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DateTimePicker onChange={dateOnChange} value={datePickerValueFormatter(value)} slotProps={{ textField: { size: "small" } }} disabled={disabled} />
                    </LocalizationProvider>
                );
            }
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        slotProps={{
                            textField: ({ position }) => ({
                                label: position === "start" ? "From" : "To",
                                variant: "standard",
                            }),
                        }}
                        value={formatValues()}
                        onChange={dateRangeOnChange}
                    />
                </LocalizationProvider>
            );
        }
        case "CHECKBOX":
            return <Checkbox size="small" onChange={checkBoxOnChange} value={field} checked={!!value} disabled={disabled} />;
        default:
            console.error("AqbCustomValueEditor: inputType is not supported", inputType);
            return null;
    }
});

type CustomFieldSelectorOptions = CustomFieldSelectorOption[];
type CustomFieldSelectorOption = {
    label: string;
    value: string;
    name: string;
    inputType: string;
};
